
































import Vue from "vue";
import { Component, Prop, Ref } from "vue-property-decorator";

import { VCard, VIcon } from "vuetify/lib";
import { ContentLink, ContentWarning } from "sitewhere-ide-components";

import {
  IDatastoreDefinition,
  IDatastoreDefinitionLocal,
  IDatastoreDefinitionReference
} from "sitewhere-configuration-model";
import {
  IInstanceConfiguration,
  ITimeSeriesConfigurationMap,
  ITimeSeriesConfiguration
} from "sitewhere-rest-api";

import TsDatastoreCreateDialog from "./TsDatastoreCreateDialog.vue";
import TsDatastoreUpdateDialog from "./TsDatastoreUpdateDialog.vue";
import Warp10Summary from "./warp10/Warp10Summary.vue";
import InfluxDbSummary from "./influxdb/InfluxDbSummary.vue";
import NoSummary from "./NoSummary.vue";

@Component({
  components: {
    VCard,
    VIcon,
    ContentLink,
    ContentWarning,
    TsDatastoreCreateDialog,
    TsDatastoreUpdateDialog,
    Warp10Summary,
    InfluxDbSummary,
    NoSummary
  }
})
export default class TsDatastoreSelector extends Vue {
  @Prop() readonly datastore!: IDatastoreDefinition;
  @Prop() readonly instance!: IInstanceConfiguration;
  @Ref() readonly create!: TsDatastoreCreateDialog;
  @Ref() readonly update!: TsDatastoreUpdateDialog;

  @Ref() readonly warp10!: Warp10Summary;
  @Ref() readonly influxdb!: InfluxDbSummary;
  @Ref() readonly unknown!: NoSummary;

  /** Get displayed summary panel */
  get summary(): Vue {
    if (this.isWarp10) {
      return this.warp10;
    } else if (this.isInfluxDB) {
      return this.influxdb;
    } else {
      return this.unknown;
    }
  }

  /** Global time series configurations */
  get timeSeriesConfigurations(): ITimeSeriesConfigurationMap | null {
    return this.instance && this.instance.persistenceConfigurations
      ? this.instance.persistenceConfigurations.timeSeriesConfigurations
      : null;
  }

  /** Get reference name */
  get datastoreReference(): string | null {
    return this.datastore
      ? (this.datastore as IDatastoreDefinitionReference).reference
      : null;
  }

  /** Local or global datastore type */
  get datastoreType(): string | null {
    const reference = this.datastoreReference;
    if (reference) {
      const global: IDatastoreDefinition | null = this.findGlobalDefinition(
        reference
      );
      return global ? global.type : null;
    }
    return this.datastore
      ? (this.datastore as IDatastoreDefinitionLocal).type
      : null;
  }

  /** Local or global datastore configuration */
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  get configuration(): any {
    const reference = this.datastoreReference;
    if (reference) {
      const global: IDatastoreDefinition | null = this.findGlobalDefinition(
        reference
      );
      return global ? global.configuration : null;
    }
    return this.datastore
      ? (this.datastore as IDatastoreDefinitionLocal).configuration
      : null;
  }

  /** Warp 10 datastore */
  get isWarp10(): boolean {
    return this.datastoreType ? this.datastoreType == "warp10" : false;
  }

  /** InfluxDB datastore */
  get isInfluxDB(): boolean {
    return this.datastoreType ? this.datastoreType == "influxdb" : false;
  }

  /** Unknown datastore */
  get isUnknown(): boolean {
    return !this.isWarp10 && !this.isInfluxDB;
  }

  /** Find a referenced persistence configuration */
  findGlobalDefinition(reference: string): IDatastoreDefinitionLocal | null {
    if (!this.timeSeriesConfigurations) {
      return null;
    }
    const match: ITimeSeriesConfiguration = this.timeSeriesConfigurations[
      reference
    ];
    if (!match) {
      return null;
    }
    return {
      type: match.type,
      configuration: match.configuration
    };
  }

  /** Add datastore */
  onAddDatastore() {
    this.create.openDialog();
  }

  /** Called to create datastore based on UI data */
  onDatastoreCreate(definition: IDatastoreDefinition) {
    this.$emit("create", definition);
  }

  /** Update datastore */
  onUpdateDatastore() {
    this.update.openDialog(this.datastore);
  }

  /** Called to update datastore based on UI data */
  onDatastoreUpdate(definition: IDatastoreDefinition) {
    this.$emit("update", definition);
  }

  /** Unset datastore */
  onUnsetDatastore() {
    this.$emit("unset");
  }
}
